<template>
  <Card class="overflow-hidden">
    <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
      <div>
        <Heading :level="3" class="mb-1">
          {{ queueTitle }}
        </Heading>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ queueDescription }}
        </p>
      </div>

      <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
        {{ totalLabel }}
      </span>
    </div>

    <div
      v-if="actorTypes.length"
      class="flex flex-wrap gap-2 border-b border-gray-200 px-6 py-3 dark:border-gray-700"
    >
      <button
        v-for="tab in actorTypes"
        :key="tab.value"
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition"
        :class="actorType === tab.value
          ? 'bg-primary-500 text-white'
          : tab.count > 0
            ? 'border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/20'
            : 'border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800'"
        :disabled="loadingList"
        @click="selectType(tab.value)"
      >
        <span>{{ tab.label }}</span>
        <span
          v-if="tab.count > 0"
          class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none"
          :class="actorType === tab.value
            ? 'bg-white/20 text-white'
            : 'bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950'"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <LoadingView :loading="loadingList">
      <div
        v-if="!items.length"
        class="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ emptyLabel }}
      </div>

      <div v-else class="divide-y divide-gray-100 dark:divide-gray-800">
        <button
          v-for="item in items"
          :key="`${item.actor_type}-${item.id}`"
          type="button"
          class="flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-start transition hover:bg-gray-50 dark:hover:bg-gray-800/60"
          @click="openItem(item)"
        >
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{ item.name }}
            </p>
            <p class="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
              {{ item.type_label }}
              <span v-if="item.country"> · {{ localized(item.country) }}</span>
              <span v-if="item.email"> · {{ item.email }}</span>
            </p>
          </div>

          <span
            class="inline-flex shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide"
            :class="item.ready_for_final_approval
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'"
          >
            {{ itemBadge(item) }}
          </span>
        </button>
      </div>

      <div
        v-if="lastPage > 1"
        class="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-3 dark:border-gray-700"
      >
        <button
          type="button"
          class="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200"
          :disabled="page <= 1 || loadingList"
          @click="goToPage(page - 1)"
        >
          {{ __('Previous') }}
        </button>

        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ __('Page :current of :last', { current: page, last: lastPage }) }}
        </span>

        <button
          type="button"
          class="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200"
          :disabled="page >= lastPage || loadingList"
          @click="goToPage(page + 1)"
        >
          {{ __('Next') }}
        </button>
      </div>
    </LoadingView>

    <ResponsiveConfirmDialog
      :show="modalOpen"
      :title="reviewTitle"
      size="3xl"
      modal-style="window"
      :use-focus-trap="false"
      @close="closeModal"
      @close-via-escape="closeModal"
    >
      <div
        class="panel-confirm-dialog max-md:!bg-transparent overflow-hidden rounded-3xl border border-zinc-200/70 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900 dark:max-md:!bg-transparent max-md:rounded-none max-md:border-0 max-md:shadow-none"
      >
        <ModalHeader class="max-md:hidden flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-base font-semibold text-zinc-900 dark:text-white">
              {{ reviewTitle }}
            </p>
            <p
              v-if="reviewMeta"
              class="mt-1 truncate text-xs text-zinc-500 dark:text-zinc-400"
            >
              {{ reviewMeta }}
            </p>
          </div>

          <button
            type="button"
            class="rounded-xl p-2 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            :aria-label="__('Close')"
            @click="closeModal"
          >
            <Icon type="x-mark" />
          </button>
        </ModalHeader>

        <ModalContent class="max-h-[min(70vh,36rem)] overflow-y-auto px-6 py-4 max-md:max-h-none max-md:!px-0 max-md:!py-1">
          <LoadingView :loading="loadingReview">
            <div
              v-if="reviewError"
              class="mx-0 rounded-2xl bg-red-50 px-3.5 py-3 text-sm text-red-600 dark:bg-red-900/30 dark:text-red-300 max-md:mx-0"
            >
              {{ reviewError }}
            </div>

            <div
              v-else-if="review && !review.has_documents"
              class="flex flex-col gap-4 max-md:-mx-3"
            >
              <dl class="grid gap-3 rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-4 text-sm dark:border-zinc-700/80 dark:bg-zinc-800/40 max-md:p-3.5">
                <div class="flex flex-wrap justify-between gap-2">
                  <dt class="text-zinc-500 dark:text-zinc-400">{{ __('Name') }}</dt>
                  <dd class="font-semibold text-zinc-900 dark:text-white">{{ review.actor?.name }}</dd>
                </div>
                <div v-if="review.actor?.email" class="flex flex-wrap justify-between gap-2">
                  <dt class="text-zinc-500 dark:text-zinc-400">{{ __('Email') }}</dt>
                  <dd class="text-zinc-900 dark:text-white">{{ review.actor.email }}</dd>
                </div>
                <div v-if="review.actor?.phone" class="flex flex-wrap justify-between gap-2">
                  <dt class="text-zinc-500 dark:text-zinc-400">{{ __('Phone') }}</dt>
                  <dd class="text-zinc-900 dark:text-white">{{ review.actor.phone }}</dd>
                </div>
                <div v-if="review.actor?.country" class="flex flex-wrap justify-between gap-2">
                  <dt class="text-zinc-500 dark:text-zinc-400">{{ __('Country') }}</dt>
                  <dd class="text-zinc-900 dark:text-white">{{ localized(review.actor.country) }}</dd>
                </div>
                <div v-if="review.actor?.verification_note" class="flex flex-col gap-1">
                  <dt class="text-zinc-500 dark:text-zinc-400">{{ __('Verification Note') }}</dt>
                  <dd class="text-zinc-900 dark:text-white">{{ review.actor.verification_note }}</dd>
                </div>
              </dl>

              <input
                v-model="accountRejectReason"
                type="text"
                class="form-control form-input min-h-10 w-full rounded-xl"
                :placeholder="__('Rejection reason')"
              >
            </div>

            <div
              v-else-if="review"
              class="flex flex-col gap-4 max-md:-mx-3 max-md:gap-4"
            >
              <article
                v-for="item in review.requirements"
                :key="item.id"
                class="rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-4 dark:border-zinc-700/80 dark:bg-zinc-800/40 max-md:rounded-2xl max-md:p-3.5"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0 flex-1 space-y-1">
                    <p class="text-sm font-semibold text-zinc-900 dark:text-white">
                      {{ localized(item.name) }}
                      <span
                        v-if="item.is_required"
                        class="ms-1 text-xs font-medium text-red-500"
                      >*</span>
                    </p>
                    <p
                      v-if="item.description"
                      class="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400"
                    >
                      {{ localized(item.description) }}
                    </p>
                  </div>

                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold"
                    :class="statusClass(item.document?.status)"
                  >
                    {{ statusLabel(item.document?.status) }}
                  </span>
                </div>

                <div v-if="item.document" class="mt-4 space-y-3">
                  <button
                    v-if="item.document.is_image"
                    type="button"
                    class="group relative block w-full overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 text-start dark:border-zinc-700 dark:bg-zinc-900"
                    @click="openImagePreview(item.document, localized(item.name))"
                  >
                    <img
                      :src="item.document.file_url"
                      :alt="localized(item.name)"
                      class="h-36 w-full object-cover transition duration-200 group-hover:scale-[1.02] sm:h-40"
                      loading="lazy"
                    >
                    <span
                      class="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2.5 pt-8 text-xs font-semibold text-white"
                    >
                      <span>{{ __('Tap to enlarge') }}</span>
                      <span class="opacity-80">v{{ item.document.version }}</span>
                    </span>
                  </button>

                  <a
                    v-else
                    :href="item.document.file_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-xs font-semibold text-primary-600 no-underline transition hover:border-primary-300 hover:bg-primary-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-primary-400 dark:hover:border-primary-500/40 dark:hover:bg-primary-500/10 sm:w-auto sm:justify-start"
                  >
                    <Icon type="arrow-top-right-on-square" class="h-3.5 w-3.5" />
                    <span>{{ __('View file') }}</span>
                    <span class="text-zinc-400">v{{ item.document.version }}</span>
                  </a>

                  <p
                    v-if="item.document.reject_reason"
                    class="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-900/30 dark:text-red-300"
                  >
                    {{ item.document.reject_reason }}
                  </p>

                  <div
                    v-if="item.document.status === 'pending'"
                    class="grid gap-2 sm:grid-cols-[auto_1fr_auto] sm:items-center"
                  >
                    <button
                      type="button"
                      class="order-2 min-h-10 rounded-xl bg-green-600 px-4 text-xs font-semibold text-white transition hover:bg-green-500 disabled:opacity-50 sm:order-1"
                      :disabled="busy"
                      @click="approveDocument(item.document.id)"
                    >
                      {{ __('Approve Document') }}
                    </button>

                    <input
                      v-model="rejectReasons[item.document.id]"
                      type="text"
                      class="form-control form-input order-1 min-h-10 w-full rounded-xl sm:order-2"
                      :placeholder="__('Rejection reason')"
                    >

                    <button
                      type="button"
                      class="order-3 min-h-10 rounded-xl bg-red-600 px-4 text-xs font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"
                      :disabled="busy"
                      @click="rejectDocument(item.document.id)"
                    >
                      {{ __('Reject Document') }}
                    </button>
                  </div>
                </div>

                <p
                  v-else
                  class="mt-3 rounded-xl border border-dashed border-amber-300/70 bg-amber-50/70 px-3 py-2 text-xs text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                >
                  {{ __('No document uploaded yet.') }}
                </p>
              </article>
            </div>
          </LoadingView>
        </ModalContent>

        <ModalFooter class="max-md:hidden">
          <div class="flex w-full flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-zinc-500 dark:text-zinc-400">
              {{ reviewFooterHint }}
            </p>

            <div class="panel-confirm-footer-actions !flex-row !items-center">
              <button
                type="button"
                class="min-h-10 rounded-xl border border-zinc-200 px-4 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                :disabled="busy"
                @click="closeModal"
              >
                {{ __('Close') }}
              </button>

              <button
                v-if="review?.has_documents === false"
                type="button"
                class="min-h-10 rounded-xl bg-red-600 px-4 text-xs font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"
                :disabled="busy"
                @click="rejectAccount"
              >
                {{ __('Reject Account') }}
              </button>

              <button
                type="button"
                class="min-h-10 rounded-xl bg-primary-500 px-4 text-xs font-semibold text-white transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="busy || (review?.has_documents && !review?.all_required_approved)"
                @click="approveAccount"
              >
                {{ review?.has_documents === false ? __('Approve Account') : __('Final Company Approval') }}
              </button>
            </div>
          </div>
        </ModalFooter>
      </div>

      <template #footer>
        <div class="space-y-3 px-1 pb-1">
          <p class="text-center text-xs text-zinc-500 dark:text-zinc-400">
            {{ reviewFooterHint }}
          </p>

          <div class="panel-confirm-footer-actions panel-confirm-footer-actions--sheet">
            <button
              type="button"
              class="min-h-10 flex-1 rounded-xl border border-zinc-200 px-4 text-xs font-semibold text-zinc-700 dark:border-zinc-700 dark:text-zinc-200"
              :disabled="busy"
              @click="closeModal"
            >
              {{ __('Close') }}
            </button>

            <button
              v-if="review?.has_documents === false"
              type="button"
              class="min-h-10 flex-1 rounded-xl bg-red-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              :disabled="busy"
              @click="rejectAccount"
            >
              {{ __('Reject Account') }}
            </button>

            <button
              type="button"
              class="min-h-10 flex-1 rounded-xl bg-primary-500 px-4 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="busy || (review?.has_documents && !review?.all_required_approved)"
              @click="approveAccount"
            >
              {{ review?.has_documents === false ? __('Approve Account') : __('Final Company Approval') }}
            </button>
          </div>
        </div>
      </template>
    </ResponsiveConfirmDialog>

    <Teleport to="body">
      <div
        v-if="previewImage"
        class="fixed inset-0 z-[800] flex flex-col bg-black/90"
        role="dialog"
        aria-modal="true"
        @keydown.esc.stop="closeImagePreview"
      >
        <div class="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          <p class="min-w-0 truncate text-sm font-semibold text-white">
            {{ previewImage.title || __('Document preview') }}
          </p>

          <button
            type="button"
            class="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
            @click.stop="closeImagePreview"
          >
            <Icon type="x-circle" class="h-4 w-4" />
            <span>{{ __('Close') }}</span>
          </button>
        </div>

        <button
          type="button"
          class="flex min-h-0 flex-1 cursor-zoom-out items-center justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
          :aria-label="__('Close')"
          @click="closeImagePreview"
        >
          <img
            :src="previewImage.file_url"
            :alt="previewImage.title || __('Document preview')"
            class="max-h-full max-w-full rounded-xl object-contain"
            @click.stop
          >
        </button>
      </div>
    </Teleport>
  </Card>
</template>

<script>
export default {
  props: {
    /**
     * Nova card payload
     */
    card: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      /**
       * Actor types the current admin may review
       */
      actorTypes: [],

      /**
       * Active queue tab
       */
      actorType: null,

      /**
       * Whether the queue list is loading
       */
      loadingList: false,

      /**
       * Whether the review modal payload is loading
       */
      loadingReview: false,

      /**
       * Whether a review mutation is in flight
       */
      busy: false,

      /**
       * Accounts on the current page
       */
      items: [],

      /**
       * Current page number
       */
      page: 1,

      /**
       * Last available page
       */
      lastPage: 1,

      /**
       * Total accounts in the current tab
       */
      total: 0,

      /**
       * Whether the review modal is open
       */
      modalOpen: false,

      /**
       * Selected account id for the modal
       */
      selectedId: null,

      /**
       * Admin review payload for the selected account
       */
      review: null,

      /**
       * Modal load/action error message
       */
      reviewError: '',

      /**
       * Rejection reasons keyed by document id
       */
      rejectReasons: {},

      /**
       * Account-level rejection reason for non-KYB actors
       */
      accountRejectReason: '',

      /**
       * Image document currently opened in the enlarge modal
       */
      previewImage: null,
    }
  },

  computed: {
    /**
     * Label of the active actor tab
     *
     * @returns {string}
     */
    activeTypeLabel() {
      return this.actorTypes.find((tab) => tab.value === this.actorType)?.label || ''
    },

    /**
     * Queue heading scoped to the active actor tab
     *
     * @returns {string}
     */
    queueTitle() {
      if (this.activeTypeLabel) {
        return this.__(':type Awaiting Verification', { type: this.activeTypeLabel })
      }

      return this.__('Accounts Awaiting Verification')
    },

    /**
     * Queue subtitle depending on whether the tab uses KYB documents
     *
     * @returns {string}
     */
    queueDescription() {
      return this.actorType === 'company'
        ? this.__('Companies that submitted required documents and are waiting for admin review.')
        : this.__('Accounts waiting for admin approval.')
    },

    /**
     * Empty-state copy for the active tab
     *
     * @returns {string}
     */
    emptyLabel() {
      if (this.activeTypeLabel) {
        return this.__('No :type are currently awaiting verification.', { type: this.activeTypeLabel })
      }

      return this.__('No accounts are currently awaiting verification.')
    },

    /**
     * Total count label shown in the card header
     *
     * @returns {string}
     */
    totalLabel() {
      return this.__('Total: :count', { count: this.total })
    },

    /**
     * Title shown in the review dialog / bottom sheet
     *
     * @returns {string}
     */
    reviewTitle() {
      return this.review?.actor?.name
        || this.review?.company?.name
        || this.__('Actor Verification')
    },

    /**
     * Secondary account meta line for the review header
     *
     * @returns {string}
     */
    reviewMeta() {
      const actor = this.review?.actor || this.review?.company

      if (!actor) {
        return ''
      }

      return [
        actor.type_label,
        actor.country ? this.localized(actor.country) : null,
        actor.email,
      ].filter(Boolean).join(' · ')
    },

    /**
     * Footer hint depending on document review progress
     *
     * @returns {string}
     */
    reviewFooterHint() {
      if (this.review?.has_documents === false) {
        return this.__('Approve or reject this account.')
      }

      return this.review?.all_required_approved
        ? this.__('All required documents are approved.')
        : this.__('Approve every required document to enable final company approval.')
    },
  },

  /**
   * Load available tabs and the first queue page when the card mounts
   */
  async mounted() {
    await this.fetchTypes()
  },

  /**
   * Clean up the enlarge-preview listeners
   */
  beforeUnmount() {
    window.removeEventListener('keydown', this.handlePreviewKeydown)
    window.document.body.classList.remove('overflow-hidden')
  },

  methods: {
    /**
     * Resolve a translatable name for the active locale
     *
     * @param {string|Record<string, string>|null} value
     * @returns {string}
     */
    localized(value) {
      if (!value) {
        return ''
      }

      if (typeof value === 'string') {
        return value
      }

      const locale = Nova.config('locale') || 'en'

      return value[locale] || value.en || Object.values(value)[0] || ''
    },

    /**
     * Map a document status to badge classes
     *
     * @param {string|null|undefined} status
     * @returns {string}
     */
    statusClass(status) {
      if (status === 'approved') {
        return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
      }

      if (status === 'rejected') {
        return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      }

      if (status === 'pending') {
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      }

      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    },

    /**
     * Human label for a document status
     *
     * @param {string|null|undefined} status
     * @returns {string}
     */
    statusLabel(status) {
      if (status === 'approved') {
        return this.__('Approved')
      }

      if (status === 'rejected') {
        return this.__('Rejected')
      }

      if (status === 'pending') {
        return this.__('Pending')
      }

      return this.__('Missing')
    },

    /**
     * Badge copy for a queue row
     *
     * @param {{ ready_for_final_approval?: boolean, has_documents?: boolean }} item
     * @returns {string}
     */
    itemBadge(item) {
      if (item.has_documents === false) {
        return this.__('Awaiting approval')
      }

      return item.ready_for_final_approval
        ? this.__('Ready for final approval')
        : this.__('Needs document review')
    },

    /**
     * Load actor types the current admin may review
     *
     * @param {{ preserveSelection?: boolean }} [options]
     * @returns {Promise<void>}
     */
    async fetchTypes(options = {}) {
      const preserveSelection = options.preserveSelection === true

      try {
        const { data } = await Nova.request().get('/nova-vendor/company-verification/types')
        this.actorTypes = data.data || []

        if (!preserveSelection) {
          this.actorType = this.actorTypes[0]?.value || null
        }

        if (this.actorType) {
          await this.fetchQueue(this.page || 1, { refreshCounts: false })
        }
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to load verification queue.'))
      }
    },

    /**
     * Refresh pending counts on every actor tab
     *
     * @returns {Promise<void>}
     */
    async refreshTypeCounts() {
      try {
        const { data } = await Nova.request().get('/nova-vendor/company-verification/types')
        const countsByType = Object.fromEntries(
          (data.data || []).map((tab) => [tab.value, tab.count ?? 0]),
        )

        this.actorTypes = this.actorTypes.map((tab) => ({
          ...tab,
          count: countsByType[tab.value] ?? 0,
        }))
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to load verification queue.'))
      }
    },

    /**
     * Switch the active actor tab
     *
     * @param {string} type
     * @returns {Promise<void>}
     */
    async selectType(type) {
      if (type === this.actorType || this.loadingList) {
        return
      }

      this.actorType = type
      this.page = 1
      await this.fetchQueue(1)
    },

    /**
     * Fetch a page of accounts awaiting verification
     *
     * @param {number} [page]
     * @param {{ refreshCounts?: boolean }} [options]
     * @returns {Promise<void>}
     */
    async fetchQueue(page = this.page, options = {}) {
      const refreshCounts = options.refreshCounts !== false

      if (!this.actorType) {
        this.items = []
        this.total = 0
        this.lastPage = 1

        return
      }

      this.loadingList = true

      try {
        const { data } = await Nova.request().get('/nova-vendor/company-verification/queue', {
          params: {
            page,
            per_page: 10,
            type: this.actorType,
          },
        })

        this.items = data.data || []
        this.page = data.current_page || page
        this.lastPage = data.last_page || 1
        this.total = data.total || 0

        const activeTab = this.actorTypes.find((tab) => tab.value === this.actorType)

        if (activeTab) {
          activeTab.count = this.total
        }

        if (refreshCounts) {
          await this.refreshTypeCounts()
        }
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to load verification queue.'))
      } finally {
        this.loadingList = false
      }
    },

    /**
     * Navigate to another queue page
     *
     * @param {number} page
     * @returns {Promise<void>}
     */
    async goToPage(page) {
      if (page < 1 || page > this.lastPage) {
        return
      }

      await this.fetchQueue(page)
    },

    /**
     * Open the review modal for an account
     *
     * @param {{ id: number, actor_type?: string }} item
     * @returns {Promise<void>}
     */
    async openItem(item) {
      this.selectedId = item.id
      this.actorType = item.actor_type || this.actorType
      this.modalOpen = true
      this.review = null
      this.reviewError = ''
      this.rejectReasons = {}
      this.accountRejectReason = ''
      await this.fetchReview(item.id)
    },

    /**
     * Close the review modal
     */
    closeModal() {
      this.modalOpen = false
      this.selectedId = null
      this.review = null
      this.reviewError = ''
      this.rejectReasons = {}
      this.accountRejectReason = ''
      this.closeImagePreview()
    },

    /**
     * Open the enlarge modal for an image document
     *
     * @param {{ file_url: string, title?: string }} file
     * @param {string} [title]
     */
    openImagePreview(file, title = '') {
      this.previewImage = {
        file_url: file.file_url,
        title: title || file.title || this.reviewTitle,
      }

      window.document.body.classList.add('overflow-hidden')
      Nova.pauseShortcuts()
      window.addEventListener('keydown', this.handlePreviewKeydown)
    },

    /**
     * Close the enlarge image modal
     */
    closeImagePreview() {
      if (!this.previewImage) {
        return
      }

      this.previewImage = null
      window.document.body.classList.remove('overflow-hidden')
      Nova.resumeShortcuts()
      window.removeEventListener('keydown', this.handlePreviewKeydown)
    },

    /**
     * Close the enlarge modal when Escape is pressed
     *
     * @param {KeyboardEvent} event
     */
    handlePreviewKeydown(event) {
      if (event.key === 'Escape') {
        this.closeImagePreview()
      }
    },

    /**
     * Load the selected account for review
     *
     * @param {number} id
     * @returns {Promise<void>}
     */
    async fetchReview(id) {
      this.loadingReview = true
      this.reviewError = ''

      try {
        const { data } = await Nova.request().get(
          `/nova-vendor/company-verification/actors/${this.actorType}/${id}`,
        )

        this.review = data.data
      } catch (error) {
        this.reviewError = error?.response?.data?.message
          || this.__('Unable to load company documents.')
      } finally {
        this.loadingReview = false
      }
    },

    /**
     * Approve a pending document
     *
     * @param {number} documentId
     * @returns {Promise<void>}
     */
    async approveDocument(documentId) {
      this.busy = true

      try {
        const { data } = await Nova.request().post(
          `/nova-vendor/company-verification/documents/${documentId}/approve`,
        )

        this.review = data.data
        Nova.success(data.message || this.__('Document approved successfully.'))
        await this.fetchQueue(this.page)
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to approve document.'))
      } finally {
        this.busy = false
      }
    },

    /**
     * Reject a pending document with a reason
     *
     * @param {number} documentId
     * @returns {Promise<void>}
     */
    async rejectDocument(documentId) {
      const reason = (this.rejectReasons[documentId] || '').trim()

      if (!reason) {
        Nova.error(this.__('A rejection reason is required.'))

        return
      }

      this.busy = true

      try {
        const { data } = await Nova.request().post(
          `/nova-vendor/company-verification/documents/${documentId}/reject`,
          { reason },
        )

        this.review = data.data
        Nova.success(data.message || this.__('Document rejected successfully.'))
        await this.fetchQueue(this.page)
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to reject document.'))
      } finally {
        this.busy = false
      }
    },

    /**
     * Approve the selected account
     *
     * @returns {Promise<void>}
     */
    async approveAccount() {
      if (!this.selectedId || !this.actorType) {
        return
      }

      if (this.review?.has_documents && !this.review?.all_required_approved) {
        return
      }

      this.busy = true

      try {
        const { data } = await Nova.request().post(
          `/nova-vendor/company-verification/actors/${this.actorType}/${this.selectedId}/approve`,
        )

        Nova.success(data.message || this.__('Account verified successfully.'))
        this.closeModal()
        await this.fetchQueue(this.page)
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to verify account.'))
      } finally {
        this.busy = false
      }
    },

    /**
     * Reject a pending non-KYB account
     *
     * @returns {Promise<void>}
     */
    async rejectAccount() {
      const reason = (this.accountRejectReason || '').trim()

      if (!reason) {
        Nova.error(this.__('A rejection reason is required.'))

        return
      }

      if (!this.selectedId || !this.actorType) {
        return
      }

      this.busy = true

      try {
        const { data } = await Nova.request().post(
          `/nova-vendor/company-verification/actors/${this.actorType}/${this.selectedId}/reject`,
          { reason },
        )

        Nova.success(data.message || this.__('Account rejected successfully.'))
        this.closeModal()
        await this.fetchQueue(this.page)
      } catch (error) {
        Nova.error(error?.response?.data?.message || this.__('Unable to reject account.'))
      } finally {
        this.busy = false
      }
    },
  },
}
</script>
